import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SearchableSelect,
  Separator,
  useToast,
} from "@racoon/ui";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { IcDeleteSmall } from "@racoon/icons";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useDeletePickupLocationGroupMutation,
  useUpdatePickupLocationGroupMutation,
} from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { useListCompatibleTrucksForPickupLocationGroupQuery } from "~/shared/api/truck/truck.grpc";
import { TruckCapabilitiesModal } from "~/features/truck-capabilities/ui/truck-capabilities.modal";
import { AutoGenerateTimeField, DEFAULT_AUTO_GENERATE_TIME } from "./auto-generate-time.field";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: ConnectProto.PickupLocationGroup;
  /** Full truck list, used to display the currently assigned truck even when
   *  it is not compatible with the group's current bindings. */
  trucks: ConnectProto.Truck[];
  onDeleted?: () => void;
};

export const EditGroupModal = ({ open, onOpenChange, group, trucks, onDeleted }: Props) => {
  const t = useT();
  const { toast } = useToast();
  const [name, setName] = useState(group.name);
  const [truckId, setTruckId] = useState<bigint | null>(group.defaultTruckId || null);
  const [autoGenerateEnabled, setAutoGenerateEnabled] = useState(!!group.autoGenerateTime);
  const [autoGenerateTime, setAutoGenerateTime] = useState(
    group.autoGenerateTime || DEFAULT_AUTO_GENERATE_TIME,
  );
  const [capabilitiesOpen, setCapabilitiesOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [updateGroup, { isLoading }] = useUpdatePickupLocationGroupMutation();
  const [deleteGroup, { isLoading: isDeleting }] = useDeletePickupLocationGroupMutation();

  // Only trucks whose capability matrix covers every bound
  // (container type, job type) link of this group are offered.
  const { data: compatibleData } = useListCompatibleTrucksForPickupLocationGroupQuery(
    open
      ? { pickupLocationGroupId: group.id, ownerOrganizationId: group.ownerOrganizationId }
      : skipToken,
  );

  const selectableTrucks = useMemo(() => {
    const compatible = compatibleData?.trucks ?? [];
    // Keep the currently assigned truck visible even if no longer compatible.
    const current = trucks.find((truck) => truckId !== null && truck.id === truckId);
    if (current && !compatible.some((truck) => truck.id === current.id)) {
      return [current, ...compatible];
    }
    return compatible;
  }, [compatibleData, trucks, truckId]);

  const selectedTruck = useMemo(
    () => trucks.find((truck) => truckId !== null && truck.id === truckId) ?? null,
    [trucks, truckId],
  );

  useEffect(() => {
    if (open) {
      setName(group.name);
      setTruckId(group.defaultTruckId || null);
      // Reset on every open, so a time picked for another group never carries over.
      setAutoGenerateEnabled(!!group.autoGenerateTime);
      setAutoGenerateTime(group.autoGenerateTime || DEFAULT_AUTO_GENERATE_TIME);
    }
  }, [open, group]);

  const canSubmit = name.trim().length > 0 && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await updateGroup({
        id: group.id,
        // Required by the permify check: access is verified against the
        // group's owner organization taken from the request.
        ownerOrganizationId: group.ownerOrganizationId,
        groupName: name.trim(),
        ...(truckId !== null ? { defaultTruckId: truckId } : {}),
        // "" turns generation off.
        autoGenerateTime: autoGenerateEnabled ? autoGenerateTime : "",
      }).unwrap();
      toast({ title: t(Namespace.PickupLocation, "groups.notifications.groupUpdated") });
      onOpenChange(false);
    } catch {
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "groups.notifications.error"),
        description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroup({
        id: group.id,
        ownerOrganizationId: group.ownerOrganizationId,
      }).unwrap();
      toast({
        title: t(Namespace.PickupLocation, "groups.notifications.groupDeleted"),
        description: group.name,
      });
      setDeleteOpen(false);
      onOpenChange(false);
      onDeleted?.();
    } catch {
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "groups.notifications.error"),
        description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!gap-0 !p-0 w-[600px] max-w-[600px] overflow-hidden rounded-2xl border-border-primary">
        <DialogHeader className="!px-4">
          <DialogTitle className="text-xl font-bold tracking-tight">
            {t(Namespace.PickupLocation, "groups.editGroup")}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-4">
            <Label
              htmlFor="edit-group-name"
              className="w-[200px] shrink-0 text-sm font-medium tracking-[-0.2px] text-text-body"
            >
              {t(Namespace.PickupLocation, "groups.groupName")}
            </Label>
            <Input
              id="edit-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(Namespace.PickupLocation, "groups.groupNamePlaceholder")}
              className="h-8 min-w-0 flex-1 rounded-lg border-border-primary px-2 text-sm text-text-body"
              autoFocus
            />
          </div>

          <div className="flex w-full flex-col gap-4 rounded-lg border border-border-primary bg-slate-100 p-4">
            <div className="flex items-center gap-4">
              <Label className="w-[200px] shrink-0 text-sm font-medium tracking-[-0.2px] text-text-body">
                {t(Namespace.PickupLocation, "groups.responsibleTruck")}
              </Label>
              <div className="min-w-0 flex-1">
                <SearchableSelect
                  id="edit-group-truck-search"
                  match="compact"
                  options={selectableTrucks.map((truck) => ({
                    id: String(truck.id),
                    name: truck.plateNumber ?? "",
                  }))}
                  value={truckId !== null ? String(truckId) : ""}
                  onChange={(id) => setTruckId(BigInt(id))}
                  placeholder={t(Namespace.PickupLocation, "groups.noTruck")}
                  searchPlaceholder={t(Namespace.PickupLocation, "groups.searchTruck")}
                  triggerClassName="h-8 w-full rounded-lg border-border-primary bg-white px-2"
                  contentClassName="w-64"
                />
              </div>
            </div>

            {selectedTruck?.truckType && (
              <div className="flex items-center justify-between gap-4">
                <span className="flex flex-wrap gap-2 text-sm tracking-[-0.01px] text-icon-secondary">
                  <span>
                    {selectedTruck.truckType.manufacturer} {selectedTruck.truckType.model}
                  </span>
                  {!!selectedTruck.truckType.type && (
                    <span>
                      {t(
                        Namespace.Enum,
                        `TRUCK_TYPE_${ConnectProto.TruckType[selectedTruck.truckType.type]}` as TranslationKey,
                      )}
                    </span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 shrink-0 rounded-lg px-3 text-sm font-medium tracking-[-0.2px] text-text-information hover:text-text-information"
                  onClick={() => setCapabilitiesOpen(true)}
                >
                  {t(Namespace.PickupLocation, "groups.truckCapabilities")}
                </Button>
              </div>
            )}
          </div>

          <AutoGenerateTimeField
            id="edit-group-auto-generate-time"
            enabled={autoGenerateEnabled}
            time={autoGenerateTime}
            onEnabledChange={setAutoGenerateEnabled}
            onTimeChange={setAutoGenerateTime}
          />
        </div>

        <Separator />

        <DialogFooter className="!justify-between p-4">
          <Button
            variant="solidRed"
            className="h-8 gap-1 rounded-lg px-3 text-sm font-medium tracking-[-0.2px]"
            onClick={() => setDeleteOpen(true)}
            disabled={isDeleting}
          >
            <IcDeleteSmall className="size-4" />
            {t(Namespace.PickupLocation, "groups.deleteGroup")}
          </Button>
          <Button
            className="h-8 rounded-lg bg-surface-action px-3 text-sm font-medium tracking-[-0.2px]"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {t(Namespace.Buttons, "save")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <TruckCapabilitiesModal
        open={capabilitiesOpen}
        onOpenChange={setCapabilitiesOpen}
        truck={selectedTruck}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="!gap-0 !p-0 overflow-hidden rounded-2xl sm:max-w-md">
          <DialogHeader className="!px-4">
            <DialogTitle className="text-xl font-bold tracking-tight">
              {t(Namespace.PickupLocation, "groups.deleteGroup")}
            </DialogTitle>
            <DialogDescription>
              {t(Namespace.PickupLocation, "groups.deleteGroupConfirm", { name: group.name })}
            </DialogDescription>
          </DialogHeader>
          <Separator />
          <DialogFooter className="p-4">
            <Button
              variant="outline"
              className="h-9 rounded-lg px-5"
              onClick={() => setDeleteOpen(false)}
              disabled={isDeleting}
            >
              {t(Namespace.PickupLocation, "groups.unbindModal.cancel")}
            </Button>
            <Button
              variant="opacityRedWithoutOutline"
              className="h-9 rounded-lg px-5"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {t(Namespace.PickupLocation, "groups.deleteGroup")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
